import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://clickandgard.fr";
const SITE_NAME = "Click&Gard";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const DEFAULT_DESCRIPTION =
  "Trouvez une garde d'enfants de confiance en quelques clics. Profils vérifiés, réservation en ligne, paiement sécurisé. 100% gratuit pour les parents.";

interface SeoProps {
  /** Page title (without the site suffix). Will become "title — Click&Gard". */
  title: string;
  /** Meta description (recommended < 160 chars). */
  description?: string;
  /** Absolute URL of OG image. Defaults to /og-image.jpg. */
  image?: string;
  /** Canonical path (e.g. "/search"). Defaults to current pathname. */
  path?: string;
  /** Open Graph type. Defaults to "website". */
  type?: "website" | "article" | "profile";
  /** Set to true on private pages (auth, dashboard...). */
  noIndex?: boolean;
  /** Optional JSON-LD structured data. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const Seo = ({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_OG_IMAGE,
  path,
  type = "website",
  noIndex = false,
  jsonLd,
}: SeoProps) => {
  const location = useLocation();
  const canonicalPath = path ?? location.pathname;
  const canonical = `${SITE_URL}${canonicalPath}`;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`;
  // OG/Twitter titles are best kept short — strip the suffix length awareness.
  const truncatedDesc = description.length > 160 ? `${description.slice(0, 157)}...` : description;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={truncatedDesc} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDesc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} — ${title}`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDesc} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} — ${title}`} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default Seo;
